import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { CommandPalette } from "./components/palette/CommandPalette";
// CHG-1283 — first-visit welcome trigger. Mounts globally; renders the
// 4-step WelcomeOverlay once per browser per the v2-welcome-* localStorage
// flags. Hides itself otherwise.
import { WelcomeMount } from "./components/welcome/WelcomeMount";
// CHG-1287 — dual-host adoption telemetry. Fires session_started on mount,
// page_view on every route change; consumers (welcome, classic-link) emit
// ad-hoc events via emitAdoptionEvent. Privacy-by-design — no PII.
import { AdoptionTelemetry } from "./telemetry/AdoptionTelemetry";
// CHG-1330 — bulk-tag dialog host. Mounts once at App root; listens to
// the openBulkTagDialog imperative emitter (called from ListPage bulkAction
// onAction handlers). Closes DRIFT-648.
import { BulkTagDialogHost } from "./lib/bulk-tag-dialog";
import { AppShell } from "./components/shell/AppShell";
import { Overview } from "./pages/Overview";
import { Donors } from "./pages/Donors";
import { DonorDetail } from "./pages/DonorDetail";
import { StripeAccounts } from "./pages/StripeAccounts";
import { StripeAccountDetail } from "./pages/StripeAccountDetail";
import { Assets } from "./pages/Assets";
import { AssetDetail } from "./pages/AssetDetail";
import { Campaigns } from "./pages/Campaigns";
import { CampaignDetail } from "./pages/CampaignDetail";
import { RecurringDonations } from "./pages/RecurringDonations";
import { RecurringDonationDetail } from "./pages/RecurringDonationDetail";
import { Settings } from "./pages/Settings";
// CHG-1494 / ITER-3612 (donor-portal β.5) — donor self-edit profile page.
// Consumes GET+PATCH /api/v1/me/profile (CHG-1478 α.1).
import { MyProfile } from "./pages/MyProfile";
// CHG-1498 / ITER-3616 (donor-portal β.6) — donor self-edit preferences
// page. Consumes GET+PATCH /api/v1/me/preferences (CHG-1490 α.6).
import { MyPreferences } from "./pages/MyPreferences";
// CHG-1519 / ITER-3638 (donor-portal wave 2) — v2 ports of v1's
// donor-detail surfaces. Backend stays at /api/v1/my-giving/*; URL
// scheme is the v2-canonical /me/* (see api/my-giving.ts).
import { DonorOnboarding } from "./pages/DonorOnboarding";
import { MyRecurringDetail } from "./pages/MyRecurringDetail";
import { MyPledgeDetail } from "./pages/MyPledgeDetail";
import { Donations } from "./pages/Donations";
// CHG-2061 / ITER-4159 (kiosk-γ.1) — dashboard-v2 Kiosks list page.
// γ.2 ships KioskDetail in a parallel worker; detail import lands
// in that CHG.
import { Kiosks } from "./pages/Kiosks";
// CHG-2460 / ITER-905202 (Widget Studio S3) — Widget Studio instance
// list + detail under the Channels nav group. Lifecycle actions + the
// presentation Edit dialog are driven by WidgetInstanceActionsHost
// (mounted once below, sibling to the other modal hosts).
import { Widgets } from "./pages/Widgets";
import { WidgetDetail } from "./pages/WidgetDetail";
import { WidgetAnalyticsDashboard } from "./pages/WidgetAnalyticsDashboard";
import { WidgetInstanceActionsHost } from "./lib/widget-instance-actions";
// CHG-2463 / ITER-905205 (Widget Studio S4) — standalone installations list +
// detail; inline status-override driven by WidgetInstallationActionsHost.
import { WidgetInstallations } from "./pages/WidgetInstallations";
// CHG-2471 / ITER-905213 (Widget Studio S5) — read-only page-observations list
// + definition catalog (list + detail). All read-only; no action host needed.
import { WidgetObservations } from "./pages/WidgetObservations";
import { WidgetCatalog } from "./pages/WidgetCatalog";
import { WidgetDefinitionDetail } from "./pages/WidgetDefinitionDetail";
import { WidgetInstallationDetail } from "./pages/WidgetInstallationDetail";
import { WidgetInstallationActionsHost } from "./lib/widget-installation-actions";
// RDM-R08 — recurring-donation lifecycle action host (pause/resume/cancel/
// reconcile). Imperative emitter driven from the recurring detail config.
import { RecurringDonationActionsHost } from "./lib/recurring-donation-actions";
// CHG-1671 — v2 donate-page admin pages (DonatePages list /
// DonatePageDetail / DonatePageCreate) retired with the backend admin
// CRUD surface (DonatePageConfig is now a system-managed entity-intrinsic
// projection; no user-facing admin authoring). Was CHG-1638 / ITER-3751.
import { MyGiving } from "./pages/MyGiving";
import { MyPledges } from "./pages/MyPledges";
import { MyReceipts } from "./pages/MyReceipts";
import { MyImpact } from "./pages/MyImpact";
import { MyCalendar } from "./pages/MyCalendar";
import { MyOrgDetail } from "./pages/MyOrgDetail";
import { MyDonationDetail as MyDonationDetailPortal } from "./pages/MyDonationDetail";
import { MyCommunications } from "./pages/MyCommunications";
import { MyFundraisingPages } from "./pages/MyFundraisingPages";
import { Reports } from "./pages/Reports";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { AnnualReports } from "./pages/AnnualReports";
import { ImpactReports } from "./pages/ImpactReports";
import { DonationDetail } from "./pages/DonationDetail";
import { TeamMembers } from "./pages/TeamMembers";
import { TeamMemberDetail } from "./pages/TeamMemberDetail";
// CHG-1663 / ITER-3772 — Roles v2 port (generator-emitted via
// role.entity-hint.yaml + page wrappers + override-block modals).
import { Roles } from "./pages/Roles";
import { RoleDetail } from "./pages/RoleDetail";
import { Events } from "./pages/Events";
import { EventDetail } from "./pages/EventDetail";
import { EventCreate } from "./pages/EventCreate";
import { EventEdit } from "./pages/EventEdit";
import { Messages } from "./pages/Messages";
import { MessageDetail } from "./pages/MessageDetail";
import { Pledges } from "./pages/Pledges";
import { PledgeDetail } from "./pages/PledgeDetail";
import { Designations } from "./pages/Designations";
import { DesignationDetail } from "./pages/DesignationDetail";
import { Templates } from "./pages/Templates";
import { TemplateDetail } from "./pages/TemplateDetail";
// CHG-2062 / ITER-4160 (kiosk-γ.2) — v2 KioskDetail page (5 tabs +
// inline CHG-1198 kiosk-mode settings panel). γ.1 (Kiosks list) lands
// in parallel; PM merge sequence unions the imports.
import { KioskDetail } from "./pages/KioskDetail";
import { Imports } from "./pages/Imports";
import { ImportDetail } from "./pages/ImportDetail";
import { Tags } from "./pages/Tags";
import { TagDetail } from "./pages/TagDetail";
// CHG-1307 — platform-admin v2 Adoption telemetry dashboard (internal).
import { V2AdoptionDashboard } from "./pages/platform/V2AdoptionDashboard";
// CHG-1540 / ITER-3658 — IA correction. PlatformDashboard is the
// platform-admin home at /platform. Consumes /api/v1/platform/*
// stats. Mounted via the RoleAwareRedirect bounce for is_platform_admin
// accounts (and directly via the Sidebar Platform group).
import { PlatformDashboard } from "./pages/PlatformDashboard";
// CHG-1551 / ITER-3668 — shared SOON stub for the 5 forwardPromised
// /platform/* routes. Field-test report B3 + N3 closure.
import { PlatformStubPage } from "./pages/PlatformStubPage";
// CHG-2572 / ITER-905309 (DRIFT-960) — generic SOON stub for non-platform
// forwardPromised nav surfaces. Reserves /signage so the Events-group
// "Signage" nav row (forwardPromised, no v2 page yet) deep-links to a
// "coming soon" panel instead of the generic NotFound 404.
import { ComingSoonPage } from "./pages/ComingSoonPage";
// CHG-1416 / ITER-3536 — v2 NotFound catch-all (DRIFT-649 close). Mounted
// as the final route inside the AppShell-wrapped nested <Routes> block so
// users hitting unknown URLs see a 404 panel inside the shell chrome
// instead of an empty content slot.
import { NotFound } from "./pages/NotFound";
// CHG-1643 / ITER-3753 (Wave 1 Tier 1) — port of v1 ActivityFeed +
// AuditLogs + DeliveryLogs to v2. Org-scoped at /activity, /audit-logs,
// /delivery-logs; platform-scoped variants of activity + audit at
// /platform/activity, /platform/audit-logs (+ /platform/audit alias).
// Replaces the CHG-1551 PlatformStubPage entries for those two paths.
import { ActivityFeed, PlatformActivityFeed } from "./pages/ActivityFeed";
import { AuditLogs, PlatformAuditLogs } from "./pages/AuditLogs";
import { DeliveryLogs } from "./pages/DeliveryLogs";
import { ProtectedRoute } from "./components/ProtectedRoute";
// CHG-1504 / ITER-3622 — γ.2 donor-portal RoleAwareRedirect. Bounces
// donor-only accounts from "/" → "/my-giving" and admin-only accounts from
// "/my-giving" → "/" so each role lands on its populated overview surface.
// Mounted INSIDE <AppShell> wrapping the nested <Routes> so auth pages
// (outside AppShell) bypass it.
import { RoleAwareRedirect } from "./components/RoleAwareRedirect";
// CHG-1496 / ITER-3614 — β.2 donor-portal page. Route /me/recurring.
// Consumes CHG-1485 (α.3) GET /api/v1/me/recurring-donations.
import { MyRecurring } from "./pages/MyRecurring";
// CHG-1501 / ITER-3618 — γ.1 donor-portal Overview at /me.
// Consumes CHG-1491 (α.7) GET /api/v1/me/overview. γ.2 (donor-only →
// /me, admin-only → / redirect) is a separate CHG; this slice just
// mounts the /me page.
import { MyOverview } from "./pages/MyOverview";

// CHG-1277 — v2 auth pages. Routed OUTSIDE <AppShell> + no <ProtectedRoute>
// wrapping (public surfaces; the URL's token is the auth carrier). Backend
// auth.py emits these URLs in transactional emails; without these pages
// every reset/verify/invite/sso/register link 404s post-cutover. Resolves
// CHG-1275 BLOCKER #1 (μ-CHG dispatch blocker).
// CHG-1353 / ITER-3478 — Phase 3B migration: CHG-1277 hand-rolled
// auth pages retired, replaced with CHG-1327 generator-emitted siblings
// (auth-flow YAML → tools/v2-gen/v2genauth.py → *.generated.tsx).
// Per CHG-1345 the generator now covers the full functional contract;
// see specs/_product/v2-auth-migration-parity-audit.md for closeout.
// CHG-2958 (Slice J / UM-R12, partial DRIFT-1153 closure) — reset-password
// routes the HAND-WRITTEN carrier again: the auth-flow generator cannot
// branch on response fields (DRIFT-1153), so the generated twin treated
// the 200 {mfa_required} response as terminal success WITHOUT changing
// the password. The carrier implements the /auth/reset-password/mfa-verify
// second step; the generated twin stays as the regen target until the
// response-branch construct lands.
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import VerifyEmailPage from "./pages/auth/verify-email.generated";
// CHG-2964 (DRIFT-1153 branch 2) — accept-invite routes the HAND-WRITTEN
// carrier (mirrors the branch-1 reset-password fix above). The auth-flow
// generator cannot branch UI on a response field (DRIFT-1153), so the
// generated twin rendered the password form for EVERY 200 from GET
// /auth/invite-status — including {status: accepted|expired|invalid}
// envelopes — letting a dead token present a submittable form that only
// failed at POST. The carrier branches per invite-status state (v1
// AcceptInvitePage / BUG-877 / CHG-0474). The generated twin
// (accept-invite.generated.tsx) stays as the regen target and must NOT be
// re-routed until the v2genauth response-branch construct lands (DRIFT-1156).
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import SsoCallbackPage from "./pages/auth/sso-callback.generated";
import RegisterFromEmailPage from "./pages/auth/register-from-email.generated";
// CHG-1461 / ITER-3577 — hand-rolled LoginPage. Termination state is
// "navigate" not "render success UI", so it stays outside the auth-flow
// generator. See AuthContext.tsx for the matching rationale.
import LoginPage from "./pages/auth/LoginPage";
// CHG-1510 / ITER-3628 (MFA program η.1) — MFA verification page for
// the mfa_required path. Reached from LoginPage via navigate
// /mfa-challenge carrying mfa_token + mfa_methods + returnTo in
// location.state. See AuthContext LoginResult discriminated union.
import MfaChallengePage from "./pages/auth/MfaChallengePage";
// CHG-1512 / ITER-3629 (MFA program η.2) — MFA setup page for the
// mfa_setup_required path. Sibling of MfaChallengePage; both routed
// outside AppShell (the setup_token / mfa_token IS the auth carrier).
import MfaSetupPage from "./pages/auth/MfaSetupPage";

// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md:
// the root <App /> router is the entry point that wires the generator output;
// it cannot itself be generated. Future routes are added by importing from
// @generated/screens (when the v2 FE generator emits them).
//
// Dev-only routes (CHG-1179): the lazy import below uses import.meta.env.DEV,
// which Vite statically replaces at build time. In production builds the
// `false` branch is dead code and Rollup tree-shakes the demo module out of
// the bundle entirely. Same pattern reused for the command-palette demo
// route (ITER-3345 / CHG-1223) and the shell-demo route below (ITER-3354 / CHG-1231).
//
// AppShell mount (CHG-1231): wraps <Routes> so every route renders inside the
// sidebar + top-bar chrome. The CommandPalette stays OUTSIDE the shell so its
// modal renders at document-root z-index without being clipped by the shell's
// flex/grid layout.
//
// Routes that should opt OUT of the shell (e.g., future auth/login pages that
// fill the viewport) belong in a separate <Routes> block sibling to <AppShell>,
// not inside it. No auth routes today — pattern is documented for when ε
// CHG-1170 lands real auth.

const StagePrimitivesDemo = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/_dev/stage-primitives-demo").then((m) => ({
        default: m.StagePrimitivesDemo,
      })),
    )
  : null;

const CommandPaletteDemo = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/_dev/command-palette-demo").then((m) => ({
        default: m.CommandPaletteDemo,
      })),
    )
  : null;

const ShellDemo = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/_dev/shell-demo").then((m) => ({
        default: m.ShellDemo,
      })),
    )
  : null;

const CompositionPrimitivesDemo = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/_dev/composition-primitives-demo").then((m) => ({
        default: m.CompositionPrimitivesDemo,
      })),
    )
  : null;

// CHG-1271 — surface 2: brand-aware login preview. Forward-promise target
// for ε CHG-1170 real Login.tsx. Renders var(--ag-brand-login-bg) + logo +
// wordmark from useBrand().
const AuthPreview = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/_dev/auth-preview").then((m) => ({
        default: m.AuthPreview,
      })),
    )
  : null;

export function App() {
  return (
    <>
      {/* Global ⌘K / Ctrl+K palette — mounted once, listens at window level.
          Lives outside AppShell so its modal renders at document-root z-index. */}
      <CommandPalette />
      {/* CHG-1283 — first-visit welcome overlay trigger. Sibling to the
          palette so the overlay's z-index 1000 backdrop covers AppShell.
          CHG-1649 / ITER-3758 — route-scoped via WelcomeMount: the
          trigger renders only on the landing routes /, /overview,
          /platform, /me. Non-landing routes (deep-links to /donors,
          /campaigns, etc.) never instantiate the overlay even if a
          future regression breaks the dismissed-flag check. */}
      <WelcomeMount />
      {/* CHG-1287 — adoption telemetry global mount. Returns null;
          subscribes to useLocation for page_view events. */}
      <AdoptionTelemetry />
      {/* CHG-1330 — bulk-tag dialog host. Sibling to other modal hosts so
          the overlay z-index covers AppShell. Renders null while idle. */}
      <BulkTagDialogHost />
      {/* CHG-2460 — Widget Studio lifecycle + edit dialog host. Sibling to the
          other modal hosts so overlays cover AppShell. Renders null while idle. */}
      <WidgetInstanceActionsHost />
      {/* CHG-2463 — Widget Studio installation status-override dialog host. */}
      <WidgetInstallationActionsHost />
      {/* RDM-R08 — recurring-donation lifecycle host (pause/resume/cancel/
          reconcile). Sibling to the other modal hosts; renders null while idle. */}
      <RecurringDonationActionsHost />
      <Routes>
        {/* CHG-1277 — v2 auth pages routed OUTSIDE AppShell + without
            ProtectedRoute. These render via the token in the URL; the
            user is by definition not yet authenticated. Routes registered
            BEFORE the AppShell catch-all so they match first. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* CHG-2964 (DRIFT-1153 branch 2) — state-aware hand-written carrier; the generated twin is superseded pending the v2genauth response-branch construct (DRIFT-1156). */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
        <Route path="/auth/register-from-email" element={<RegisterFromEmailPage />} />
        {/* CHG-1461 / ITER-3577 — v2 owns its own /login. App.v2 hostname is
            intentionally separate from v1 so a cross-domain auth handoff would
            be a UX regression (CORS cookie shenanigans). */}
        <Route path="/login" element={<LoginPage />} />
        {/* CHG-1510 / ITER-3628 (MFA program η.1) — MFA verification.
            Unauth-accessible (the user is mid-login when they hit this
            page; only an mfa_token from the login response distinguishes
            them). */}
        <Route path="/mfa-challenge" element={<MfaChallengePage />} />
        {/* CHG-1512 / ITER-3629 (MFA program η.2) — MFA setup page.
            Unauth-accessible because the setup_token IS the auth
            carrier (transient JWT with purpose=mfa-setup; BE validates
            via validate_mfa_setup_token). */}
        <Route path="/mfa-setup" element={<MfaSetupPage />} />

        {/* Everything else renders inside AppShell. The /* catch-all hands
            off to a nested <Routes> block — React Router 6 supports nested
            Routes when the parent uses a wildcard path. */}
        <Route
          path="/*"
          element={
            <AppShell>
              {/* CHG-1504 / ITER-3622 — γ.2 routing nudge. Wraps the
                  nested <Routes> so every AppShell-scoped path runs the
                  role check on render. Pass-through during auth bootstrap
                  + unauthenticated state (ProtectedRoute owns the
                  /login bounce); active redirects are limited to the
                  two literal landing paths ("/" for donor-only,
                  "/my-giving" for admin-only). */}
              <RoleAwareRedirect>
              <Routes>
                {/* CHG-1238 — Overview / Home, the most-trafficked v2 surface.
                    Replaces the CHG-1167 PlaceholderIndex stub that previously
                    sat at "/". CHG-1461 — wrapped in ProtectedRoute (no
                    specific permission required, just authentication) so
                    visitors without a session bounce to /login instead of
                    rendering empty Overview + a console flood of 401s.
                    CHG-1540 / ITER-3658 — IA correction. "/" is kept
                    mounted as Overview so neither-role accounts (no
                    platform admin, no org membership, no donor profile)
                    don't see a blank page; RoleAwareRedirect bounces
                    authenticated roles to /platform / /overview / /me
                    before this route renders. The canonical org admin
                    home is now /overview (registered below). */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Overview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/overview"
                  element={
                    <ProtectedRoute>
                      <Overview />
                    </ProtectedRoute>
                  }
                />
                {/* CHG-1540 / ITER-3658 — Platform admin home. ProtectedRoute
                    only gates on auth; the BE platform endpoints each
                    enforce platform-admin authorization (return 403 to
                    non-platform-admin). Sidebar Platform group visibility
                    is gated client-side on account.is_platform_admin
                    (CHG-1523) so non-platform-admin users never see a
                    link to this route. */}
                <Route
                  path="/platform"
                  element={
                    <ProtectedRoute>
                      <PlatformDashboard />
                    </ProtectedRoute>
                  }
                />
                {/* CHG-1551 / ITER-3668 — Field-test report B3 + N3
                    closure. The 5 forwardPromised Platform group items
                    (Organizations / Members / Activity / Audit logs /
                    Billing) used to 404 on deep-link / click-through
                    even though PlatformLinksCard advertised them as
                    SOON. Each now mounts PlatformStubPage which
                    renders consistent AppShell chrome + "Coming soon"
                    banner + back link to /platform. When a dedicated
                    page lands in a future CHG, swap the element prop
                    per-route — no other call-site changes. The brief
                    listed `/platform/audit`; the nav and
                    PlatformLinksCard use `/platform/audit-logs`. Both
                    paths mount the same stub so either deep link
                    works. */}
                <Route
                  path="/platform/organizations"
                  element={
                    <ProtectedRoute>
                      <PlatformStubPage
                        title="Organizations"
                        subtitle="All tenants across the platform. This management surface is coming soon — until then, the organization count + per-tenant deep-dive live in the Platform dashboard and OrgSwitcher."
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/platform/members"
                  element={
                    <ProtectedRoute>
                      <PlatformStubPage
                        title="Members"
                        subtitle="Platform-admin and org-admin accounts. This management surface is coming soon — until then, member stats live in the Platform dashboard."
                      />
                    </ProtectedRoute>
                  }
                />
                {/* CHG-1643 / ITER-3753 (Wave 1 Tier 1) — Platform
                    activity + audit. Replaces the CHG-1551 stubs for
                    these two paths. These are READ-ONLY surfaces, so the
                    FE requirePermission gate mirrors the authoritative
                    backend require_permission dependency per route
                    (activity_feed.py -> alora-giving.activity.read;
                    audit.py -> alora-giving.audit.read) — DRIFT-975 /
                    CHG-2596 corrected the prior overload of the
                    organization.create WRITE permission as a
                    platform-admin proxy. Defense-in-depth only: the
                    backend handlers additionally enforce platform-admin
                    authoritatively on scope=platform (403 otherwise), and
                    the Platform nav group is gated on
                    account.is_platform_admin, so non-platform-admins do
                    not surface these links. */}
                <Route
                  path="/platform/activity"
                  element={
                    <ProtectedRoute requirePermission="alora-giving.activity.read">
                      <PlatformActivityFeed />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/platform/audit"
                  element={
                    <ProtectedRoute requirePermission="alora-giving.audit.read">
                      <PlatformAuditLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/platform/audit-logs"
                  element={
                    <ProtectedRoute requirePermission="alora-giving.audit.read">
                      <PlatformAuditLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/platform/billing"
                  element={
                    <ProtectedRoute>
                      <PlatformStubPage
                        title="Billing"
                        subtitle="Subscription + invoice oversight across all tenants. Coming soon."
                      />
                    </ProtectedRoute>
                  }
                />
                {/* CHG-2572 / ITER-905309 (DRIFT-960) — reserve /signage.
                    The Events-group "Signage" nav item is forwardPromised
                    (non-clickable SOON row in the sidebar; v2 has no
                    signage page yet — v1 has the full SignageDevices /
                    SignageDeviceDetail surface). Without this route a
                    direct URL / deep link to /signage fell through to the
                    generic NotFound catch-all. Mounting ComingSoonPage
                    keeps the deep link on a "coming soon" panel; swap the
                    element prop when the real v2 signage page lands. */}
                <Route
                  path="/signage"
                  element={
                    <ProtectedRoute>
                      <ComingSoonPage title="Signage" />
                    </ProtectedRoute>
                  }
                />

          {/* CHG-1225 — Donors canary. Both gated by ProtectedRoute
              (stub; real auth in ε CHG-1170). */}
          <Route
            path="/donors"
            element={
              <ProtectedRoute requirePermission="alora-giving.donor.read">
                <Donors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/donors/:donorId"
            element={
              <ProtectedRoute requirePermission="alora-giving.donor.read">
                <DonorDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1642 / ITER-3752 — Stripe Accounts v1→v2 port (Wave 1
              Tier 1). Both routes gated by ProtectedRoute with the
              stripe-account.read permission. /stripe-accounts list +
              :id detail; idParam="id" per DetailPage convention. */}
          <Route
            path="/stripe-accounts"
            element={
              <ProtectedRoute requirePermission="alora-giving.stripe-account.read">
                <StripeAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stripe-accounts/:id"
            element={
              <ProtectedRoute requirePermission="alora-giving.stripe-account.read">
                <StripeAccountDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1234 — Assets canary. Same gating pattern as /donors. */}
          <Route
            path="/assets"
            element={
              <ProtectedRoute requirePermission="alora-giving.asset.read">
                <Assets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/:assetId"
            element={
              <ProtectedRoute requirePermission="alora-giving.asset.read">
                <AssetDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1236 — Campaigns canary (third v2 entity). */}
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute requirePermission="alora-giving.campaign.read">
                <Campaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId"
            element={
              <ProtectedRoute requirePermission="alora-giving.campaign.read">
                <CampaignDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1240 — Recurring donations canary (fourth v2 entity). */}
          <Route
            path="/recurring"
            element={
              <ProtectedRoute requirePermission="alora-giving.recurring-donation.read">
                <RecurringDonations />
              </ProtectedRoute>
            }
          />

          {/* CHG-1496 / ITER-3614 — β.2 donor-portal "my recurring" page.
              Donor-scoped; no requirePermission (donors lack admin perms
              and the BE endpoint authenticates via the account-derived
              donor_ids set). ProtectedRoute still gates on authenticated
              session. */}
          <Route
            path="/my-giving/recurring"
            element={
              <ProtectedRoute>
                <MyRecurring />
              </ProtectedRoute>
            }
          />

          {/* CHG-1519 / ITER-3638 (donor-portal wave 2) — recurring detail.
              Consumes GET /api/v1/my-giving/recurring-donations/:id +
              pause/resume/cancel POST endpoints. ProtectedRoute auth-only
              (donor self-service). */}
          <Route
            path="/my-giving/recurring/:recurringId"
            element={
              <ProtectedRoute>
                <MyRecurringDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1501 / ITER-3618 — γ.1 donor-portal Overview at /me.
              ProtectedRoute with no requirePermission (donor auth model —
              same precedent as β.1/β.2/β.5 sibling /me/* routes). γ.2
              wires the auth-aware "donor-only → /me default" redirect in
              a separate CHG. */}
          <Route
            path="/my-giving"
            element={
              <ProtectedRoute>
                <MyOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recurring/:recurringId"
            element={
              <ProtectedRoute requirePermission="alora-giving.recurring-donation.read">
                <RecurringDonationDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1243 — Donations canary (fifth v2 entity; point-in-time). */}
          <Route
            path="/donations"
            element={
              <ProtectedRoute requirePermission="alora-giving.donation.read">
                <Donations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/donations/:donationId"
            element={
              <ProtectedRoute requirePermission="alora-giving.donation.read">
                <DonationDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-2061 / ITER-4159 (kiosk-γ.1) — kiosk-management list.
              Spec: specs/tier-05-experience/kiosk-management-list.cross-screen.yaml.
              γ.2 (parallel worker) ships /kiosks/:kioskId detail. */}
          <Route
            path="/kiosks"
            element={
              <ProtectedRoute requirePermission="alora-giving.kiosk.read">
                <Kiosks />
              </ProtectedRoute>
            }
          />

          {/* CHG-2460 / ITER-905202 — Widget Studio (S3). Instance list +
              detail under the Channels nav group. Both gated on
              widget-platform.read; the BE re-enforces read/configure/approve
              on every admin route. Spec: specs/_product/widget-studio-ui.md. */}
          <Route
            path="/channels/widgets"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <Widgets />
              </ProtectedRoute>
            }
          />
          {/* CHG-2463 / ITER-905205 — Widget Studio (S4). Standalone
              installations list + detail. Registered BEFORE the instance
              :widgetInstanceId route so the static "installations" segment is
              unambiguous (React Router v6 ranks static over dynamic regardless,
              but order keeps intent clear). */}
          <Route
            path="/channels/widgets/installations"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetInstallations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels/widgets/installations/:widgetInstallationId"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetInstallationDetail />
              </ProtectedRoute>
            }
          />
          {/* CHG-2471 / ITER-905213 — Widget Studio (S5). Read-only
              page-observations list + the platform-global definition catalog
              (list + detail). All read-only (definitions are seed-only, no
              create). Static segments registered before the instance
              :widgetInstanceId route. */}
          <Route
            path="/channels/widgets/observations"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetObservations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels/widgets/catalog"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels/widgets/catalog/:widgetDefinitionId"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetDefinitionDetail />
              </ProtectedRoute>
            }
          />
          {/* S8 / CHG-2474 — org-wide Widget analytics dashboard. Declared
              BEFORE the :widgetInstanceId route so the static `analytics`
              segment ranks above the dynamic param. */}
          <Route
            path="/channels/widgets/analytics"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels/widgets/:widgetInstanceId"
            element={
              <ProtectedRoute requirePermission="alora-giving.widget-platform.read">
                <WidgetDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1671 — /donate-pages, /donate-pages/new, /donate-pages/:id
              v2 admin routes retired with the backend admin CRUD surface
              (DonatePages list / DonatePageCreate wizard / DonatePageDetail
              builder). Page customization is now entity-intrinsic. */}

          {/* CHG-1495 / β.1 — donor portal "My Giving" page. Authenticated
              account only (no admin permission); donor scoping enforced
              server-side by GET /api/v1/me/donations (CHG-1479 / α.2). */}
          <Route
            path="/my-giving/donations"
            element={
              <ProtectedRoute>
                <MyGiving />
              </ProtectedRoute>
            }
          />

          {/* CHG-1499 / β.3 — donor portal "My Pledges" page. Same auth
              shape as /me/giving; donor scoping enforced server-side by
              GET /api/v1/me/pledges (CHG-1488 / α.4). */}
          <Route
            path="/my-giving/pledges"
            element={
              <ProtectedRoute>
                <MyPledges />
              </ProtectedRoute>
            }
          />

          {/* CHG-1519 / ITER-3638 (donor-portal wave 2) — pledge detail.
              Consumes GET /api/v1/my-giving/pledges/:id + /pay + /timeline
              + /cancel POST endpoints. ProtectedRoute auth-only. */}
          <Route
            path="/my-giving/pledges/:pledgeId"
            element={
              <ProtectedRoute>
                <MyPledgeDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1519 / ITER-3638 (donor-portal wave 2) — donor
              onboarding welcome. GET /api/v1/my-giving/onboarding-status
              + POST /complete-onboarding. ProtectedRoute auth-only;
              already-onboarded accounts auto-redirect to /my-giving. */}
          <Route
            path="/my-giving/onboarding"
            element={
              <ProtectedRoute>
                <DonorOnboarding />
              </ProtectedRoute>
            }
          />

          {/* CHG-1503 / β.4 — donor portal "My Receipts" page. Consumes
              GET /api/v1/me/receipts (CHG-1481 / α.5) for the paged catalog
              + GET /api/v1/me/receipts/{id}/pdf for per-receipt re-issue. */}
          <Route
            path="/my-giving/receipts"
            element={
              <ProtectedRoute>
                <MyReceipts />
              </ProtectedRoute>
            }
          />

          {/* CHG-1517 wave 2 — donor portal Impact + per-org detail +
              per-donation detail. All consume portal.py canonical
              /api/v1/my-giving/* endpoints. Route placement after the
              other /my-giving/* routes; the dynamic-param routes
              (:orgId / :donationId) come after their static siblings to
              avoid React Router path-matching surprises. */}
          <Route
            path="/my-giving/impact"
            element={
              <ProtectedRoute>
                <MyImpact />
              </ProtectedRoute>
            }
          />
          {/* comp-ε GAP-3a (CHG-2403) — donor cross-org calendar agenda +
              iCal subscribe. Consumes donor_portal_calendar.py
              /api/v1/my-giving/calendar/*. Static route, no param collision. */}
          <Route
            path="/my-giving/calendar"
            element={
              <ProtectedRoute>
                <MyCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-giving/org/:orgId"
            element={
              <ProtectedRoute>
                <MyOrgDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-giving/donations/:donationId"
            element={
              <ProtectedRoute>
                <MyDonationDetailPortal />
              </ProtectedRoute>
            }
          />

          {/* CHG-1521 wave 3 — last 2 v1-parity pages.
              MyCommunications inbox + MyFundraisingPages peer-to-peer list. */}
          <Route
            path="/my-giving/communications"
            element={
              <ProtectedRoute>
                <MyCommunications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-giving/fundraising"
            element={
              <ProtectedRoute>
                <MyFundraisingPages />
              </ProtectedRoute>
            }
          />

          {/* CHG-1659 — Reports + Analytics admin port (wave 1 / tier 1).
              4 admin pages under /reports/* gated by alora-giving.analytics.read.
              Spec authority: specs/tier-05-experience/{reports,analytics-dashboard,
              annual-reports,impact-reports}.entity-hint.yaml. Documented
              hand-roll exception per DRIFT-702 (v2gen.py has no dashboard_v2/
              menu_v2 emit modes yet). */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute requirePermission="alora-giving.analytics.read">
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/analytics"
            element={
              <ProtectedRoute requirePermission="alora-giving.analytics.read">
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/annual"
            element={
              <ProtectedRoute requirePermission="alora-giving.analytics.read">
                <AnnualReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/impact"
            element={
              <ProtectedRoute requirePermission="alora-giving.analytics.read">
                <ImpactReports />
              </ProtectedRoute>
            }
          />

          {/* CHG-1249 — Team Members canary (account; point-in-time per CHG-1235).
              6th ε canary. NO StageChip / NO ambient row (mirrors CHG-1243). */}
          <Route
            path="/team"
            element={
              <ProtectedRoute requirePermission="alora-giving.user.read">
                <TeamMembers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/:accountId"
            element={
              <ProtectedRoute requirePermission="alora-giving.user.read">
                <TeamMemberDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1663 / ITER-3772 (Wave 1 / Tier 1) — Roles v2 port.
              Generator-emitted scaffolds + page wrappers. Read permission
              is alora-giving.user.read per src/api/app/routers/roles.py
              (the GET endpoint gate). Write gates (user.create/update,
              role.delete) are enforced server-side; the modals surface
              the buttons and the backend rejects unauthorized writes. */}
          <Route
            path="/roles"
            element={
              <ProtectedRoute requirePermission="alora-giving.user.read">
                <Roles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:id"
            element={
              <ProtectedRoute requirePermission="alora-giving.user.read">
                <RoleDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1254 — Events canary (9th ε canary; point-in-time per CHG-1235;
              mirrors CHG-1243/1251 forbidden_patterns gating). */}
          <Route
            path="/events"
            element={
              <ProtectedRoute requirePermission="alora-giving.event.read">
                <Events />
              </ProtectedRoute>
            }
          />
          {/* CHG-2450 (GAP-3b-ii-b) — static /events/new MUST precede the
              /events/:eventId param route so "new" is not captured as an id. */}
          <Route
            path="/events/new"
            element={
              <ProtectedRoute requirePermission="alora-giving.event.create">
                <EventCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/edit"
            element={
              <ProtectedRoute requirePermission="alora-giving.event.update">
                <EventEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId"
            element={
              <ProtectedRoute requirePermission="alora-giving.event.read">
                <EventDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1262 — Messages canary (10th ε canary; 5th POINT-IN-TIME
              per CHG-1237 intent-vs-delivery framing). NO StageChip /
              NO ambient row / NO ProgressBar — mirrors CHG-1243/1249/1251/1254. */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute requirePermission="alora-giving.message.read">
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:messageId"
            element={
              <ProtectedRoute requirePermission="alora-giving.message.read">
                <MessageDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1248 — Pledges canary (lifecycle-rich). */}
          <Route
            path="/pledges"
            element={
              <ProtectedRoute requirePermission="alora-giving.pledge.read">
                <Pledges />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pledges/:pledgeId"
            element={
              <ProtectedRoute requirePermission="alora-giving.pledge.read">
                <PledgeDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1251 — Designations canary (point-in-time + conditional ProgressBar #4). */}
          <Route
            path="/designations"
            element={
              <ProtectedRoute requirePermission="alora-giving.designation.read">
                <Designations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designations/:designationId"
            element={
              <ProtectedRoute requirePermission="alora-giving.designation.read">
                <DesignationDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1261 — Notification Templates canary (eighth v2 entity;
              point-in-time; view-only Editor tab; Bindings from template-bridge). */}
          <Route
            path="/templates"
            element={
              <ProtectedRoute requirePermission="alora-giving.template.read">
                <Templates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:templateId"
            element={
              <ProtectedRoute requirePermission="alora-giving.template.read">
                <TemplateDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1260 — Imports canary (also-8th v2 entity by allocation; landed
              alongside Templates). Triggers the import-map wizard from CHG-1247;
              this CHG also ships the 2 real custom-render components
              (ImportColumnMappingTable + ImportPreviewTable) the wizard's map +
              preview steps require. */}
          <Route
            path="/imports"
            element={
              <ProtectedRoute requirePermission="alora-giving.import.read">
                <Imports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/imports/:importBatchId"
            element={
              <ProtectedRoute requirePermission="alora-giving.import.read">
                <ImportDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1266 — Tags canary (11th + FINAL ε entity).
              FORWARD-PROMISED: T2 entity + T4 API + permissions seed are
              Phase B-γ work per CHG-1237. Page renders against entity-
              hint contract; api/tags.ts uses safeFetch wrappers that
              return empty envelopes on 404 so the page never crashes.
              Permission code is aspirational (alora-giving.tag.read);
              when the tag-management permissions seed lands the code
              resolves canonically. Until then ProtectedRoute permits
              navigation (stub always allows). */}
          <Route
            path="/tags"
            element={
              <ProtectedRoute requirePermission="alora-giving.tag.read">
                <Tags />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tags/:tagId"
            element={
              <ProtectedRoute requirePermission="alora-giving.tag.read">
                <TagDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-2062 / ITER-4160 (kiosk-γ.2) — KioskDetail page. The
              /kiosks list route lands with γ.1; here we only mount the
              :id detail. ProtectedRoute gates on kiosk.read; the BE
              re-enforces the same permission on /kiosks/{id} and friends. */}
          <Route
            path="/kiosks/:id"
            element={
              <ProtectedRoute requirePermission="alora-giving.kiosk.read">
                <KioskDetail />
              </ProtectedRoute>
            }
          />

          {/* CHG-1307 — platform-admin v2 Adoption telemetry dashboard.
              Internal surface; gated on alora-giving.organization.create
              (a platform-admin-only code per src/api/app/core/deps.py
              _PERMISSION_ROLE_MAP) so the FE pre-emptively redirects
              non-admins to AccessDeniedPage. The BE
              /api/v1/platform/v2-adoption endpoint enforces the same
              gate authoritatively via _require_platform_admin. */}
          <Route
            path="/platform/v2-adoption"
            element={
              <ProtectedRoute requirePermission="alora-giving.organization.create">
                <V2AdoptionDashboard />
              </ProtectedRoute>
            }
          />

          {/* CHG-1239 — Settings shell. ?section=<id> URL state. */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute requirePermission="alora-giving.organization.update">
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* CHG-1643 / ITER-3753 (Wave 1 Tier 1) — org-scoped activity
              feed, audit trail, delivery log. Backend permissions are
              alora-giving.activity.read / alora-giving.audit.read /
              alora-giving.delivery-log.read; FE ProtectedRoute pre-gates
              on the same codes so non-staff roles bounce to AccessDenied
              instead of seeing a 403 toast after the page mounts. */}
          <Route
            path="/activity"
            element={
              <ProtectedRoute requirePermission="alora-giving.activity.read">
                <ActivityFeed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute requirePermission="alora-giving.audit.read">
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery-logs"
            element={
              <ProtectedRoute requirePermission="alora-giving.delivery-log.read">
                <DeliveryLogs />
              </ProtectedRoute>
            }
          />

          {/* CHG-1494 / ITER-3612 (donor-portal β.5) — donor self-edit
              profile. Auth-only (no admin permission required — a donor
              owns their own profile). ProtectedRoute without
              requirePermission gates on authentication only. */}
          <Route
            path="/my-giving/profile"
            element={
              <ProtectedRoute>
                <MyProfile />
              </ProtectedRoute>
            }
          />

          {/* CHG-1498 / ITER-3616 (donor-portal β.6) — donor self-edit
              preferences (locale + contact channel + intent topics).
              Auth-only sibling to /me/profile. */}
          <Route
            path="/my-giving/preferences"
            element={
              <ProtectedRoute>
                <MyPreferences />
              </ProtectedRoute>
            }
          />

          {StagePrimitivesDemo && (
            <Route
              path="/_dev/stage-primitives"
              element={
                <Suspense fallback={null}>
                  <StagePrimitivesDemo />
                </Suspense>
              }
            />
          )}
          {CommandPaletteDemo && (
            <Route
              path="/_dev/command-palette"
              element={
                <Suspense fallback={null}>
                  <CommandPaletteDemo />
                </Suspense>
              }
            />
          )}
          {ShellDemo && (
            <Route
              path="/_dev/shell"
              element={
                <Suspense fallback={null}>
                  <ShellDemo />
                </Suspense>
              }
            />
          )}
          {CompositionPrimitivesDemo && (
            <Route
              path="/_dev/composition-primitives"
              element={
                <Suspense fallback={null}>
                  <CompositionPrimitivesDemo />
                </Suspense>
              }
            />
          )}
          {AuthPreview && (
            <Route
              path="/_dev/auth-preview"
              element={
                <Suspense fallback={null}>
                  <AuthPreview />
                </Suspense>
              }
            />
          )}

                {/* CHG-1416 / ITER-3536 — catch-all NotFound. MUST be the
                    last route in this nested block; React Router 6 matches
                    by source order and `path="*"` swallows everything that
                    didn't match above. Resolves DRIFT-649. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </RoleAwareRedirect>
            </AppShell>
          }
        />
      </Routes>
    </>
  );
}
